import {
  WATER_LEVEL_MANAGEMENT_SCHEMES,
  getWMRIndexFromArray,
} from "utils/simulator";

import {
  getCellFaunaCapacity,
  type GameState,
} from "utils/game_utils";

import GridDiv from "components/Map/GridDiv";
import { useState, useMemo } from "react";
import { useGameContext } from "GameContext";

import CostBreakdown from "components/Simulation/CostBreakdown";

export function SpecificYearReport({
  year=1,
  customPlayerInfo,
  showAll,
}: {
  year?: number;
  customPlayerInfo?: any;
  showAll?: boolean;
}) {
  const {
    proposalHistory,
    agrCostHistory,
    roleGroupDataHistory,
    playerInfo: asDefaultInfo,
    selectedWmRegimes,
    gameStateHistory,
  } = useGameContext();

  const playerInfo = customPlayerInfo ? customPlayerInfo : asDefaultInfo
  const lastYear = year - 1

  const agCost = useMemo(() => {
    const agCosts = agrCostHistory[Math.max(lastYear - 1, 0)]
    if (agCosts) {
      return agCosts[`${playerInfo?.estate}-${playerInfo?.role}`]
    }
  }, [agrCostHistory, playerInfo, lastYear])

  const selectedWmRegime  = selectedWmRegimes[Math.max(lastYear - 1, 0)]

  const gameStateLen = gameStateHistory?.length ? gameStateHistory?.length - 1 : 0

  const startingGameState = gameStateHistory[ gameStateLen -  Math.max(lastYear - 1, 0)]
  const proposal = proposalHistory[Math.max(lastYear - 1, 0)]

  function convertArrayToRecord(actions: Array<any>) {
    return actions.reduce((acc, obj) => {
      const key = `${obj?.location?.x}-${obj?.location?.y}`;
      acc[key] = obj;
      return acc;
    }, {} as Record<string, any>);
  }

  function convertArrayToMURecord(actions: Array<any>) {
    return actions.reduce((acc, obj) => {
      const key = `${obj?.managementUnit}`;
      acc[key] = obj;
      return acc;
    }, {} as Record<string, any>);
  }

  const mapActions = convertArrayToRecord(proposal?.mapActions ?? []);
  const fActions = convertArrayToRecord(proposal?.faunaActions ?? []);
  const muAct = convertArrayToMURecord(proposal?.muActions ?? []);

  const newGameState = gameStateHistory[gameStateLen - Math.max(year - 1, 0)]

  function getAnimalCount(gameState?: GameState) {
    if (!gameState) {
      return { duck: 0, bittern: 0, heron: 0 };
    }
    const animalValues = {
      duck: 0,
      bittern: 0,
      heron: 0,
    }
    const wmwm = selectedWmRegime > -1 ? selectedWmRegime - 1 : 2
    const currentWM = WATER_LEVEL_MANAGEMENT_SCHEMES[wmwm]

    for (let i=0; i < gameState?.cellData.length; i++) {
      for (let j = 0; j < gameState?.cellData[i].length; j++) {
        const cell = gameState?.cellData[i][j]
        const duckCapacity = getCellFaunaCapacity(cell.x, cell.y, gameState, currentWM, "DUCK");
        const bitternCapacity = getCellFaunaCapacity(cell.x, cell.y, gameState, currentWM, "BOOMING_BITTERN");
        const heronCapacity = getCellFaunaCapacity(cell.x, cell.y, gameState, currentWM, "PURPLE_HERON_COLONY");

        animalValues.duck += duckCapacity
        animalValues.bittern += bitternCapacity
        animalValues.heron += heronCapacity
      }
    }

    return animalValues
  }


  return (
    <div className="flex flex-col">
      <h1 className="text-xl text-center">End of year {lastYear} report</h1>

      <label className="text-center text-sm py-2">{`Selected WM Regime: ${selectedWmRegime}`}</label>

      <div className="flex flex-row gap-2">
        Change in animals
        <p>duck: {getAnimalCount(newGameState).duck}</p>
        <p>bittern: {getAnimalCount(newGameState).bittern}</p>
        <p>heron: {getAnimalCount(newGameState).heron}</p>
      </div>

      <div className="flex flex-row h-[600px] w-full justify-around py-2 gap-2">
        <GridDiv
          cellData={startingGameState?.cellData}
          gameState={startingGameState}
          view={"land"}
          currentYear={year-1}
          limitToPersonal={true}
          mapActions={mapActions}
          faunaActions={fActions}
          muActions={muAct}

          showAnimals={true}//playerInfo?.role === "Conservationist"}
          showDucksOnly={false}//playerInfo?.role === "Hunter"}

          showLeft={playerInfo?.estate === "private" || playerInfo?.role === "Conservationist"}
          showRight={playerInfo?.estate === "public"}
          showFauna={playerInfo?.role === "Hunter"}
        />

        <GridDiv
          cellData={newGameState?.cellData}
          gameState={newGameState}
          view={"land"}
          currentYear={year}
          selectedWM={selectedWmRegime}

          showAnimals={true}//playerInfo?.role === "Conservationist"}
          showDucksOnly={false}//playerInfo?.role === "Hunter"}

          // mapActions={mapActions}
          // faunaActions={faunaActions}
          oldMuActions={muAct}
          prevGameState={startingGameState}
          showOldMU={true}

          showLeft={playerInfo?.estate === "private" || playerInfo?.role === "Conservationist"}
          showRight={playerInfo?.estate === "public"}
          showFauna={playerInfo?.role === "Hunter"}
        />
      </div>

      <CostBreakdown
        agCost={agCost}
        startingRoleMoney={agCost?.startingBalance ? agCost.startingBalance : 0} //startingRoleMoney}
      />
    </div>
  )
}

export default function YearReport({
  showAllHistory=false,
  fromDownload=false,
}: {
  showAllHistory?: boolean,
  fromDownload?: boolean,
}) {
  const {
    currentYear: defaultCurrentYear,
    players,
    gameStateHistory,
  } = useGameContext();


  const currentYear = fromDownload ? gameStateHistory.length : defaultCurrentYear
  

  const [displayedYear, setDisplayedYear] = useState(currentYear)
  const [displayedPlayer, setDisplayerdPlayer] = useState(null);

  if (!showAllHistory) {
    return (
      <SpecificYearReport year={currentYear} />
    )
  }


  return (
    <div className="flex flex-col">
      <h1 className="text-xl text-center">Game Report</h1>
      { fromDownload && (
        <div className="flex flex-row">
          {players.map((ply: any, i: number) => (
            <button onClick={() => setDisplayerdPlayer(ply) } className="text-xs">
              {ply.role}-{ply.estate}
            </button>
          ))}
        </div>
      )}

      <div className="flex flex-row overflow-x-auto py-4">
        { Array.from({length:currentYear}).map((_,i) => {
          if (i === 0) {
            return null
          }
          return (
            <div className="flex flex-row" key={i}>
              <button key={i}
                onClick={() => setDisplayedYear(i+1)}
                className={`${displayedYear === i+1 ? "bg-opacity-0" : "border-b bg-opacity-10"} bg-black border-t border-x border-black px-2 py-1`}
              >Year {i}</button>
              <div className="border-b border-black w-2"/>
            </div>
          )
        })}
        <div className="border-b border-black flex grow"/>
      </div>

      <SpecificYearReport year={displayedYear} customPlayerInfo={displayedPlayer}/>
    </div>
  )
}
